import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { InputField, LoadingIndicator } from '@atrocit/scl';

/**
 * Copied over from the latest version of the ATROCIT SCL, but with the typescript stripped out.
 * Once the SCL version is updated, this can be removed. The local SCL version lacks the disabled prop.
 */

export default function NewSelectField({ placeholder, loading = false, options = [], onChange = () => {}, value, disabled = false, ...props }) {
	const [ query, setQuery ] = useState(null);
	const [ highlightedIndex, setHighlightedIndex ] = useState(null);
	const [ isOpen, setIsOpen ] = useState(false);
	const hovering = useRef(false);
	const inputRef = useRef(null);

	const valueToShow = useMemo(() => {
		if (query != null) return query;
		if (value != null) {
			const selectedOption = (options ?? []).find(o => o.value == value);
			return selectedOption?.label ?? '';
		}
		return '';
	}, [ query, value, options ]);

	const onSelectItem = useCallback((option) => {
		setQuery(null);
		setHighlightedIndex(null);
		onChange(option?.value ?? null, option);
		if (inputRef.current != null) inputRef.current.blur();
		setIsOpen(false);
	}, [ onChange, options ]);

	useEffect(() => {
		setHighlightedIndex(0);
	}, [ query ]);

	const visibleOptions = (options ?? []).filter(o => o.label.toLowerCase().includes((query ?? '').toLowerCase()));

	return <div className={"scl-select-field" + (isOpen ? ' scl-select-field-open' : '')}>
		<InputField
			ref={inputRef}
			placeholder={placeholder}
			onChange={setQuery}
			value={valueToShow}
			disabled={disabled}
			onFocus={() => setIsOpen(true)}
			onBlur={() => setTimeout(() => setIsOpen(false), 0)}
			onKeyUp={(event) => {
				if (!isOpen) setIsOpen(true);
				if (event.key == 'ArrowUp') {
					event.preventDefault();
					event.stopPropagation();
					setHighlightedIndex(idx => Math.max(0, (idx ?? 0) - 1));
				} else if (event.key == 'ArrowDown') {
					event.preventDefault();
					event.stopPropagation();
					setHighlightedIndex(idx => Math.min(visibleOptions.length - 1, (idx ?? 0) + 1));
				} else if (event.key == 'Enter' && highlightedIndex != null) { // Enter
					event.preventDefault();
					event.stopPropagation();
					const option = visibleOptions[highlightedIndex];
					if (option != null) {
						onSelectItem(option);
						inputRef.current?.blur();
					}
				}
			}}
			onKeyDown={(event) => {
				if (event.key == 'Backspace' && (query == null || query == '')) { // Backspace, must happen in onKeyDown because otherwise query is updated already
					onChange(null, null);
					setQuery('');
				}
			}}
			{...props} />
		<div className="scl-select-field-icon">
			<span className="fa fa-chevron-down" />
		</div>

		<div aria-hidden={!isOpen} className="scl-select-field-dropdown" onMouseEnter={() => { hovering.current = true; }} onMouseLeave={() => { hovering.current = false; }} onMouseDown={e => e.preventDefault()}>
			{loading && <div style={{ padding: '16px', display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
				<LoadingIndicator size={'24px'} />
			</div>}
			{!loading && visibleOptions.map((option, idx) => {
				return <div
					ref={elem => (!hovering.current && highlightedIndex == idx && elem != null ? elem.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' }) : null)}
					key={option.value}
					className={"scl-select-field-option" + (highlightedIndex == idx ? ' scl-select-field-option-highlight' : '') + (option.value == value ? ' scl-select-field-option-selected' : '')}
					onMouseOver={() => setHighlightedIndex(idx)}
					onClick={() => onSelectItem(option)}>
					{option.label}
				</div>;
			})}
		</div>
	</div>;
}